import { storageKeys } from 'config/settings.json';

import { sessionManager } from 'hooks/useSessionManager/sessionManager';


import APIHandler from 'config/APIHandler';
import DataHandler from 'config/DataHandler';
import StorageHandler from 'config/StorageHandler';

const USER_TTL_MS = 2592000000;

interface LoginResponse {
  error?: boolean;
  success?: boolean;
  message?: string;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
}

interface GetUserOldResponse {
  response?: {
    sales_companion_registered?: string | null;
  };
}

export interface PerformLoginFailure {
  success: false;
  message?: string;
}
export interface PerformLoginSuccess {
  success: true;
  user: Record<string, unknown>;
  redirectToQuickRegister: boolean;
}
export type PerformLoginResult = PerformLoginFailure | PerformLoginSuccess;

/**
 * Performs login with email/password: gets tokens, fetches user, persists to storage,
 * and starts the session (or returns redirectToQuickRegister if user must complete registration).
 * Shared by the login form and post-registration auto-login.
 */
export async function performLogin(
  email: string,
  password: string,
  clientId: string,
  clientSecret: string,
): Promise<PerformLoginResult> {
  const appId = await DataHandler.matchAppId();
  const response = (await APIHandler.loginUser(email, password, clientId, clientSecret)) as LoginResponse;

  if (response.error || response.success === false) {
    return { success: false, message: response.message };
  }

  if (!response.access_token || response.refresh_token === undefined) {
    return { success: false };
  }

  StorageHandler.save(storageKeys.accessToken, response.access_token);
  StorageHandler.save(storageKeys.refreshToken, response.refresh_token, response.expires_in);

  const user = (await APIHandler.getUser(appId, response.access_token)) as Record<string, unknown> | null | undefined;
  if (!user || Object.keys(user).length === 0) {
    return { success: false };
  }

  const registeredForApp = (await APIHandler.getUserOld(appId, email)) as GetUserOldResponse | undefined;
  const needsQuickRegister =
    registeredForApp &&
    (registeredForApp.response?.sales_companion_registered === null ||
      registeredForApp.response?.sales_companion_registered === 'null');

  StorageHandler.save(storageKeys.user, user, USER_TTL_MS);

  if (needsQuickRegister) {
    return { success: true, user, redirectToQuickRegister: true };
  }

  await sessionManager.start({
    app_id: Number(appId),
    email: typeof user.email === 'string' ? user.email : email,
    device: 'pc',
  });

  return { success: true, user, redirectToQuickRegister: false };
}
